// Match Day — device state, outbox and sync (2026-10-18).
//
// The rule that matters: every change is written to localStorage SYNCHRONOUSLY, before any
// network call. The server is a copy; the device never needs it to keep working. One key per
// match holds everything (fixture, players, clock, squad, sub events, Sonra overrides) plus the
// outbox: the keys of rows changed here that the server doesn't have yet.
//
// Sync posts the dirty rows to POST /api/matchday/:id/sync (idempotent upsert by key, later
// clientUpdatedAt wins — services/matchday/sync.js) and folds the server's state back in with the
// same rule (engine.js mergeByKey). A row leaves the outbox only once the server holds a version at
// least as new as ours, so a dropped response, a timeout or a killed app just means it is sent
// again — never lost, never duplicated. Rows the server rejects (e.g. an unknown player) leave the
// outbox and are listed instead, so they can't block everything behind them.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMatchdayState, syncMatchday } from '../../api';
import { mergeByKey, isNewer, placeOnClock, correctEventTime } from './engine';

const PREFIX = 'heralabx.matchday.v1.';
const keyFor = id => `${PREFIX}match.${id}`;
const INDEX_KEY = `${PREFIX}index`;
const SYNC_TIMEOUT_MS = 15000;
const POLL_MS = 15000;

// ── localStorage ─────────────────────────────────────────────────────────────────────────────
export function loadLocal(matchId) {
  try {
    const raw = localStorage.getItem(keyFor(matchId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveLocal(state) {
  try {
    localStorage.setItem(keyFor(state.matchId), JSON.stringify(state));
    const f = state.fixture || {};
    const index = loadIndex().filter(x => x.id !== state.matchId);
    index.unshift({ id: state.matchId, matchDate: f.matchDate, opponent: f.opponent, homeAway: f.homeAway, kickoffAt: f.kickoffAt });
    localStorage.setItem(INDEX_KEY, JSON.stringify(index.slice(0, 20)));
    return true;
  } catch { return false; }
}

// Matches prepared on this device — the picker's offline fallback.
export function loadIndex() {
  try { return JSON.parse(localStorage.getItem(INDEX_KEY)) || []; } catch { return []; }
}

export function hasSessionToken() {
  try { return !!JSON.parse(localStorage.getItem('authUser'))?.sessionToken; } catch { return false; }
}

// ── Pure state helpers ───────────────────────────────────────────────────────────────────────
// A clientUpdatedAt that is always later than the row's previous one, even if the device clock
// stepped back — otherwise an edit could lose to its own older version.
function stamp(prevIso) {
  const prev = prevIso ? Date.parse(prevIso) : 0;
  return new Date(Math.max(Date.now(), prev + 1)).toISOString();
}

function uuid() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  // Never let Log sub fail: getRandomValues, else Math.random as a last resort.
  const b = c?.getRandomValues ? c.getRandomValues(new Uint8Array(16)) : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const emptyDirty = () => ({ clock: false, squad: {}, events: {}, overrides: {} });

export function dirtyCount(state) {
  if (!state) return 0;
  const d = state.dirty || emptyDirty();
  return (d.clock ? 1 : 0) + Object.keys(d.squad).length + Object.keys(d.events).length + Object.keys(d.overrides).length;
}

function markDirty(state, kind, key) {
  const dirty = { ...emptyDirty(), ...state.dirty };
  if (kind === 'clock') return { ...dirty, clock: true };
  return { ...dirty, [kind]: { ...dirty[kind], [key]: true } };
}

// A fresh device state from the server's first answer. An empty squad is seeded from the official
// lineup when there is one (written as local changes, so they sync like any other edit).
function fromServer(matchId, res) {
  let state = {
    v: 1, matchId, fixture: res.fixture, players: res.players, lineupSeed: res.lineupSeed,
    preparedAt: new Date().toISOString(), serverTimeOffsetMs: Date.parse(res.serverTime) - Date.now(),
    clock: res.clock, squad: res.squad, events: res.events, overrides: res.overrides || [], gps: res.gps || {},
    dirty: emptyDirty(), rejected: [], lastSyncAt: new Date().toISOString(),
  };
  if (!state.squad.length && res.lineupSeed) state = seedSquad(state, res.lineupSeed);
  return state;
}

export function seedSquad(state, seed) {
  let s = state;
  seed.starters.forEach(id => { s = setRole(s, id, 'starter'); });
  seed.bench.forEach(id => { s = setRole(s, id, 'bench'); });
  return s;
}

// role: 'starter' | 'bench' | null (out of the squad).
export function setRole(state, playerId, role) {
  const prev = state.squad.find(r => r.playerId === playerId);
  if (!prev && !role) return state;
  const row = prev
    ? { ...prev, role: role || prev.role, deletedAt: role ? null : stamp(), clientUpdatedAt: stamp(prev.clientUpdatedAt) }
    : { playerId, role, hidTargetOverrideM: null, deletedAt: null, clientUpdatedAt: stamp() };
  return { ...state, squad: [...state.squad.filter(r => r.playerId !== playerId), row], dirty: markDirty(state, 'squad', playerId) };
}

export function setTypedTarget(state, playerId, metres) {
  const prev = state.squad.find(r => r.playerId === playerId);
  if (!prev) return state;
  const row = { ...prev, hidTargetOverrideM: metres, clientUpdatedAt: stamp(prev.clientUpdatedAt) };
  return { ...state, squad: state.squad.map(r => (r.playerId === playerId ? row : r)), dirty: markDirty(state, 'squad', playerId) };
}

const CLOCK_STEPS = ['h1Start', 'h1End', 'h2Start', 'h2End'];

export function nextClockStep(clock) {
  return CLOCK_STEPS.find(k => !clock?.[k]) || null;
}
export function lastClockStep(clock) {
  return [...CLOCK_STEPS].reverse().find(k => clock?.[k]) || null;
}

export function advanceClock(state) {
  const step = nextClockStep(state.clock);
  if (!step) return state;
  const base = state.clock || { h1Start: null, h1End: null, h2Start: null, h2End: null };
  const clock = { ...base, [step]: new Date().toISOString(), clientUpdatedAt: stamp(state.clock?.clientUpdatedAt) };
  return { ...state, clock, dirty: markDirty(state, 'clock') };
}

export function undoClock(state) {
  const step = lastClockStep(state.clock);
  if (!step) return state;
  const clock = { ...state.clock, [step]: null, clientUpdatedAt: stamp(state.clock.clientUpdatedAt) };
  return { ...state, clock, dirty: markDirty(state, 'clock') };
}

// Stamped at the moment of pressing Log sub.
export function logSub(state, { playerOff, playerOn }) {
  if (playerOff == null && playerOn == null) return state;
  const occurredAt = new Date().toISOString();
  const event = {
    id: uuid(), occurredAt, ...placeOnClock(state.clock, occurredAt),
    playerOff: playerOff ?? null, playerOn: playerOn ?? null, deletedAt: null, clientUpdatedAt: stamp(),
  };
  return { ...state, events: [...state.events, event], dirty: markDirty(state, 'events', event.id) };
}

export function correctSub(state, id, occurredAtIso) {
  const prev = state.events.find(e => e.id === id);
  if (!prev) return state;
  const next = correctEventTime(prev, state.clock, occurredAtIso, stamp(prev.clientUpdatedAt));
  return { ...state, events: state.events.map(e => (e.id === id ? next : e)), dirty: markDirty(state, 'events', id) };
}

export function deleteSub(state, id) {
  const prev = state.events.find(e => e.id === id);
  if (!prev || prev.deletedAt) return state;
  const next = { ...prev, deletedAt: new Date().toISOString(), clientUpdatedAt: stamp(prev.clientUpdatedAt) };
  return { ...state, events: state.events.map(e => (e.id === id ? next : e)), dirty: markDirty(state, 'events', id) };
}

// Soft-deletes every sub still in the log (each one syncs as a normal later write).
export function clearAllSubs(state) {
  return state.events.filter(e => !e.deletedAt).reduce((s, e) => deleteSub(s, e.id), state);
}

// Back to before kick-off: clock cleared and every sub removed. The squad is kept.
export function resetMatch(state) {
  const s = clearAllSubs(state);
  if (!s.clock || !lastClockStep(s.clock)) return s;
  const clock = { h1Start: null, h1End: null, h2Start: null, h2End: null, clientUpdatedAt: stamp(s.clock.clientUpdatedAt) };
  return { ...s, clock, dirty: markDirty(s, 'clock') };
}

export function setOverride(state, playerId, hidM) {
  const prev = state.overrides.find(o => o.playerId === playerId);
  const row = { playerId, hidM, deletedAt: null, clientUpdatedAt: stamp(prev?.clientUpdatedAt) };
  return { ...state, overrides: [...state.overrides.filter(o => o.playerId !== playerId), row], dirty: markDirty(state, 'overrides', playerId) };
}

export function clearOverride(state, playerId) {
  const prev = state.overrides.find(o => o.playerId === playerId);
  if (!prev || prev.deletedAt) return state;
  const row = { ...prev, deletedAt: new Date().toISOString(), clientUpdatedAt: stamp(prev.clientUpdatedAt) };
  return { ...state, overrides: state.overrides.map(o => (o.playerId === playerId ? row : o)), dirty: markDirty(state, 'overrides', playerId) };
}

// ── Sync payload / merge ─────────────────────────────────────────────────────────────────────
function buildPayload(state) {
  const d = state.dirty || emptyDirty();
  return {
    clock: d.clock && state.clock ? state.clock : null,
    squad: state.squad.filter(r => d.squad[r.playerId]),
    events: state.events.filter(e => d.events[e.id]),
    overrides: state.overrides.filter(o => d.overrides[o.playerId]),
  };
}

// Server has it = it holds a version at least as new as ours.
const serverHas = (serverRow, localRow) => !!serverRow && !isNewer(localRow, serverRow);

export function applyServer(cur, res, sent) {
  const squad = mergeByKey(cur.squad, res.squad, r => r.playerId);
  const events = mergeByKey(cur.events, res.events, r => r.id);
  const overrides = mergeByKey(cur.overrides, res.overrides || [], r => r.playerId);
  const clock = !cur.clock || (res.clock && isNewer(res.clock, cur.clock)) ? (res.clock || cur.clock) : cur.clock;

  const rejectedKeys = new Set((res.sync?.rejected || []).map(r => `${r.kind}:${r.id ?? r.playerId}`));
  const prevDirty = cur.dirty || emptyDirty();
  const keep = (kind, list, keyOf, serverList, rejKind) => {
    const serverBy = new Map((serverList || []).map(r => [String(keyOf(r)), r]));
    const out = {};
    Object.keys(prevDirty[kind]).forEach(k => {
      const local = list.find(r => String(keyOf(r)) === k);
      const wasSent = sent[kind].has(k);
      if (!local) return;
      if (wasSent && rejectedKeys.has(`${rejKind}:${k}`)) return; // listed below instead
      if (!serverHas(serverBy.get(k), local)) out[k] = true;
    });
    return out;
  };
  const dirty = {
    clock: prevDirty.clock && !(res.clock && clock && !isNewer(clock, res.clock)),
    squad: keep('squad', squad, r => r.playerId, res.squad, 'squad'),
    events: keep('events', events, r => r.id, res.events, 'event'),
    overrides: keep('overrides', overrides, r => r.playerId, res.overrides, 'override'),
  };
  const rejected = [...(cur.rejected || []), ...(res.sync?.rejected || []).map(r => ({ ...r, at: new Date().toISOString() }))].slice(-50);
  return {
    ...cur, fixture: res.fixture, players: res.players, lineupSeed: res.lineupSeed, gps: res.gps || {},
    serverTimeOffsetMs: Date.parse(res.serverTime) - Date.now(),
    clock, squad, events, overrides, dirty, rejected, lastSyncAt: new Date().toISOString(),
  };
}

function classify(err) {
  const m = /API error: (\d{3})/.exec(err?.message || '');
  if (!m) return { kind: 'offline', message: 'No connection' };
  const status = Number(m[1]);
  if (status === 401) return { kind: 'auth', message: 'Session expired — log out and log in again (changes stay on this device)' };
  if (status === 403) return { kind: 'forbidden', message: 'This account has no Match Day access' };
  if (status === 404) return { kind: 'notfound', message: 'Match not found on the server' };
  return { kind: 'server', message: err.message };
}

const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new TypeError('timeout')), ms))]);

// ── The hook ─────────────────────────────────────────────────────────────────────────────────
// Mount with key={matchId} so a different match starts from a clean hook.
export function useMatchday(matchId) {
  const [state, setState] = useState(() => loadLocal(matchId));
  const ref = useRef(state);
  const [net, setNet] = useState({ status: 'idle', error: null, storageOk: true });
  const inFlight = useRef(false);
  const timer = useRef(null);

  const runSync = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setNet(n => ({ ...n, status: 'syncing' }));
    try {
      const cur = ref.current;
      if (!cur) {
        // Not prepared on this device yet: needs the server once.
        const res = await withTimeout(getMatchdayState(matchId), SYNC_TIMEOUT_MS);
        const next = fromServer(matchId, res);
        ref.current = next;
        const ok = saveLocal(next);
        setState(next);
        setNet({ status: 'ok', error: null, storageOk: ok });
        if (dirtyCount(next)) { inFlight.current = false; return runSync(); }
        return;
      }
      const payload = buildPayload(cur);
      const sent = {
        squad: new Set(payload.squad.map(r => String(r.playerId))),
        events: new Set(payload.events.map(r => String(r.id))),
        overrides: new Set(payload.overrides.map(r => String(r.playerId))),
      };
      const res = await withTimeout(syncMatchday(matchId, payload), SYNC_TIMEOUT_MS);
      // Merge into the CURRENT state (edits made while the request was out stay dirty).
      const next = applyServer(ref.current, res, sent);
      ref.current = next;
      const ok = saveLocal(next);
      setState(next);
      setNet({ status: 'ok', error: null, storageOk: ok });
    } catch (err) {
      const e = classify(err);
      setNet(n => ({ ...n, status: e.kind, error: e.message }));
    } finally {
      inFlight.current = false;
    }
  }, [matchId]);

  const scheduleSync = useCallback((delay = 250) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(runSync, delay);
  }, [runSync]);

  // Every change: compute → write to the device → render → then (and only then) try the network.
  const commit = useCallback(fn => {
    const cur = ref.current;
    if (!cur) return;
    const next = fn(cur);
    if (!next || next === cur) return;
    ref.current = next;
    const ok = saveLocal(next);
    setState(next);
    if (!ok) setNet(n => ({ ...n, storageOk: false }));
    scheduleSync();
  }, [scheduleSync]);

  useEffect(() => {
    runSync();
    const poll = setInterval(() => { if (document.visibilityState === 'visible') runSync(); }, POLL_MS);
    const onOnline = () => runSync();
    const onVisible = () => { if (document.visibilityState === 'visible') runSync(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll); clearTimeout(timer.current);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [runSync]);

  return { state, net, commit, syncNow: runSync, waiting: dirtyCount(state) };
}
